"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import type { ApiProfileMin } from "@/generated/models/ApiProfileMin";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import { getSubmissionCount, getTrophyArtworkCount } from "@/helpers/artist-activity.helpers";
import { useArtistPreviewModal, type ArtistPreviewTab } from "@/hooks/useArtistPreviewModal";
import { closeAllCustomTooltips } from "@/helpers/tooltip.helpers";
import { ArtistActivityBadge } from "./ArtistActivityBadge";
import { ArtistPreviewModal } from "./ArtistPreviewModal";
import { CurationWaveBadge } from "./CurationWaveBadge";
import type { ApiProfileClassification } from "@/generated/models/ApiProfileClassification";
import { useRouter } from "next/navigation";
interface DropAuthorBadgesProfile {
  readonly id?: string | null;
  readonly handle?: string | null;
  readonly pfp?: string | null;
  readonly banner1_color?: string | null;
  readonly banner2_color?: string | null;
  readonly cic?: number;
  readonly rep?: number;
  readonly tdh?: number;
  readonly tdh_rate?: number;
  readonly xtdh?: number;
  readonly xtdh_rate?: number;
  readonly level?: number;
  readonly primary_address?: string | null;
  readonly primary_wallet?: string | null;
  readonly archived?: boolean;
  readonly active_main_stage_submission_ids?: readonly string[] | null;
  readonly winner_main_stage_drop_ids?: readonly string[] | null;
  readonly artist_of_prevote_cards?: readonly number[] | null;
  readonly profile_wave_id?: string | null;
  readonly profile_wave_name?: string | null;
  readonly profile_wave_pfp?: string | null;
  readonly is_wave_creator?: boolean | null;
  readonly badges?: {
    readonly artist_of_main_stage_submissions?: number | null;
    readonly artist_of_memes?: number | null;
    readonly profile_wave_id?: string | null;
    readonly profile_wave_name?: string | null;
    readonly profile_wave_pfp?: string | null;
  } | null;
  readonly classification: ApiProfileClassification;
  readonly sub_classification: string | null;
}
type ApiProfileMinWithAuthorBadges = ApiProfileMin & {
  readonly badges?: DropAuthorBadgesProfile["badges"];
};
interface DropAuthorBadgesProps {
  readonly profile: DropAuthorBadgesProfile;
  readonly tooltipIdPrefix?: string | undefined;
  readonly className?: string | undefined;
  readonly size?: "default" | "compact" | undefined;
  readonly onArtistPreviewOpen?: ((params: {
    readonly user: ApiProfileMin;
    readonly initialTab: ArtistPreviewTab;
  }) => void) | undefined;
}
const DEFAULT_CONTAINER_CLASS = "tw-inline-flex tw-items-center tw-gap-x-1.5";
const getTrimmedText = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return null;
  }
  return trimmed;
};
const getProfileWaveId = (profile: DropAuthorBadgesProfile): string | null => getTrimmedText(profile.profile_wave_id) ?? getTrimmedText(profile.badges?.profile_wave_id);
const getProfileWaveName = (profile: DropAuthorBadgesProfile): string | null => getTrimmedText(profile.profile_wave_name) ?? getTrimmedText(profile.badges?.profile_wave_name);
const getProfileWavePfp = (profile: DropAuthorBadgesProfile): string | null => getTrimmedText(profile.profile_wave_pfp) ?? getTrimmedText(profile.badges?.profile_wave_pfp);
const copyArray = <T,>(value?: readonly T[] | null): T[] => value !== null && value !== undefined ? [...value] : [];
const toApiProfileMin = (profile: DropAuthorBadgesProfile): ApiProfileMinWithAuthorBadges => {
  const primaryAddress = profile.primary_address ?? profile.primary_wallet ?? "";
  const fallbackId = primaryAddress;
  const profileWaveId = getProfileWaveId(profile);
  return {
    id: profile.id ?? fallbackId,
    handle: profile.handle ?? null,
    pfp: profile.pfp ?? null,
    banner1_color: profile.banner1_color ?? null,
    banner2_color: profile.banner2_color ?? null,
    cic: profile.cic ?? 0,
    rep: profile.rep ?? 0,
    tdh: profile.tdh ?? 0,
    tdh_rate: profile.tdh_rate ?? 0,
    xtdh: profile.xtdh ?? 0,
    xtdh_rate: profile.xtdh_rate ?? 0,
    level: profile.level ?? 0,
    primary_address: primaryAddress,
    subscribed_actions: [],
    archived: profile.archived ?? false,
    active_main_stage_submission_ids: copyArray(profile.active_main_stage_submission_ids),
    winner_main_stage_drop_ids: copyArray(profile.winner_main_stage_drop_ids),
    artist_of_prevote_cards: copyArray(profile.artist_of_prevote_cards),
    profile_wave_id: profileWaveId,
    is_wave_creator: profile.is_wave_creator === true,
    classification: profile.classification,
    sub_classification: profile.sub_classification,
    badges: profile.badges
  };
};
export const DropAuthorBadges: React.FC<DropAuthorBadgesProps> = t0 => {
  const $ = _c(45);
  const {
    profile,
    tooltipIdPrefix: t1,
    className: t2,
    size: t3,
    onArtistPreviewOpen
  } = t0;
  const tooltipIdPrefix = t1 === undefined ? "author-badges" : t1;
  const className = t2 === undefined ? DEFAULT_CONTAINER_CLASS : t2;
  const size = t3 === undefined ? "default" : t3;
  const router = useRouter();
  let t4;
  if ($[0] !== profile) {
    t4 = getProfileWaveId(profile);
    $[0] = profile;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const profileWaveId = t4;
  const submissionCount = getSubmissionCount(profile);
  const trophyCount = getTrophyArtworkCount(profile);
  const hasActivityBadge = submissionCount > 0 || trophyCount > 0;
  let t5;
  if ($[2] !== profile) {
    t5 = getProfileWaveName(profile);
    $[2] = profile;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const profileWaveName = t5;
  let t6;
  if ($[4] !== profile) {
    t6 = getProfileWavePfp(profile);
    $[4] = profile;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const profileWavePfp = t6;
  const hasProfileWaveBadge = profileWaveId !== null;
  let t7;
  if ($[6] !== profile) {
    t7 = toApiProfileMin(profile);
    $[6] = profile;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const modalUser = t7;
  const {
    isModalOpen: isArtistPreviewOpen,
    activeTab,
    handleBadgeClick: handleArtistBadgeClick,
    handleTabChange: handleArtistTabChange,
    handleModalClose: handleArtistModalClose
  } = useArtistPreviewModal();
  let t8;
  if ($[8] !== handleArtistBadgeClick || $[9] !== modalUser || $[10] !== onArtistPreviewOpen) {
    t8 = tab => {
      closeAllCustomTooltips();
      if (onArtistPreviewOpen) {
        onArtistPreviewOpen({
          user: modalUser,
          initialTab: tab
        });
        return;
      }
      handleArtistBadgeClick(tab);
    };
    $[8] = handleArtistBadgeClick;
    $[9] = modalUser;
    $[10] = onArtistPreviewOpen;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const onArtistBadgeClick = t8;
  let t9;
  if ($[12] !== profileWaveId || $[13] !== router) {
    t9 = () => {
      closeAllCustomTooltips();
      if (!profileWaveId) {
        return;
      }
      router.push(getWaveRoute({
        waveId: profileWaveId,
        isDirectMessage: false,
        isApp: false
      }));
    };
    $[12] = profileWaveId;
    $[13] = router;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const onProfileWaveBadgeClick = t9;
  if (!hasActivityBadge && !hasProfileWaveBadge) {
    return null;
  }
  let t10;
  if ($[15] !== hasActivityBadge || $[16] !== onArtistBadgeClick || $[17] !== size || $[18] !== submissionCount || $[19] !== tooltipIdPrefix || $[20] !== trophyCount) {
    t10 = hasActivityBadge && <ArtistActivityBadge submissionCount={submissionCount} trophyCount={trophyCount} onBadgeClick={onArtistBadgeClick} tooltipId={`${tooltipIdPrefix}-activity`} size={size} />;
    $[15] = hasActivityBadge;
    $[16] = onArtistBadgeClick;
    $[17] = size;
    $[18] = submissionCount;
    $[19] = tooltipIdPrefix;
    $[20] = trophyCount;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== hasProfileWaveBadge || $[23] !== onProfileWaveBadgeClick || $[24] !== profileWaveId || $[25] !== profileWaveName || $[26] !== profileWavePfp || $[27] !== size || $[28] !== tooltipIdPrefix) {
    t11 = hasProfileWaveBadge && <CurationWaveBadge waveId={profileWaveId} tooltipId={`${tooltipIdPrefix}-profile-wave`} onBadgeClick={onProfileWaveBadgeClick} size={size} waveName={profileWaveName} wavePfp={profileWavePfp} />;
    $[22] = hasProfileWaveBadge;
    $[23] = onProfileWaveBadgeClick;
    $[24] = profileWaveId;
    $[25] = profileWaveName;
    $[26] = profileWavePfp;
    $[27] = size;
    $[28] = tooltipIdPrefix;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== className || $[31] !== t10 || $[32] !== t11) {
    t12 = <div className={className}>{t10}{t11}</div>;
    $[30] = className;
    $[31] = t10;
    $[32] = t11;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  let t13;
  if ($[34] !== activeTab || $[35] !== handleArtistModalClose || $[36] !== handleArtistTabChange || $[37] !== hasActivityBadge || $[38] !== isArtistPreviewOpen || $[39] !== modalUser || $[40] !== onArtistPreviewOpen) {
    t13 = hasActivityBadge && !onArtistPreviewOpen && <ArtistPreviewModal isOpen={isArtistPreviewOpen} onClose={handleArtistModalClose} user={modalUser} activeTab={activeTab} onTabChange={handleArtistTabChange} />;
    $[34] = activeTab;
    $[35] = handleArtistModalClose;
    $[36] = handleArtistTabChange;
    $[37] = hasActivityBadge;
    $[38] = isArtistPreviewOpen;
    $[39] = modalUser;
    $[40] = onArtistPreviewOpen;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== t12 || $[43] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
};